/**
 * SQL 튜닝 리포트(.md) 보기 — 문서로 그린다(제목·표·목록·코드 블록). 「원문」 으로 바꿔 마크다운 그대로 볼 수도 있다(복사용).
 * (사용자 요청 2026-10-09: 「.md 인데 텍스트로만 보인다」)
 *
 * 앱의 다른 마크다운 화면(설계서 등)과 같은 react-markdown + remark-gfm(표). 원시 HTML 은 그리지 않는다(react-markdown 기본) —
 * 리포트의 `<br>`(인덱스 목록 줄바꿈)만 화면에서 「 · 」 로 바꾼다(내려받는 .md 는 그대로).
 * 코드 블록(실행계획)은 고정폭 + 한글 2칸 상자(utils/monoWide) — 한글 이름 줄이 밀리지 않게.
 * 줄 끝 표시(「◀ D1」 「◀ [1]」)는 줄 강조 + 번호 배지로 그린다(PlanMarked — 진단 탭과 같은 모양). 「원문」 은 글 그대로.
 */
import { remarkCaseMarks } from '../../utils/caseTextMarks';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { monoWideText } from '../../utils/monoWide';
import { parseBar } from '../../utils/reductionBar';
import { figureStyleOf } from '../../utils/caseMeta';
import { MarkedPlanText, hasMarkedLines } from './PlanMarked';
import './ReportView.css';

/**
 * 감소율 막대 칸 — 「████░░ 82.4% 감소」(20칸, utils/reductionBar 의 모양)를 알아보고 색 막대로 그린다.
 * 내려받은 .md 에서는 글자 막대 그대로 읽힌다. 낱말은 화면 언어로 바꿔 보인다.
 */
function cellText(children) {
  return React.Children.toArray(children).map(c => (typeof c === 'string' || typeof c === 'number' ? String(c) : '')).join('').trim();
}

function ReductionBar({ pct, kind }) {
  const { t } = useTranslation();
  return (
    <span className={`stn-bar${kind === 'up' ? ' up' : ''}`}>
      <span className="stn-bar-track"><span className="stn-bar-fill" style={{ width: `${Math.min(100, pct)}%` }} /></span>
      <span className="stn-bar-text">{pct}% {t(`common:stn.bar.${kind}`)}</span>
    </span>
  );
}
ReductionBar.propTypes = { pct: PropTypes.number.isRequired, kind: PropTypes.oneOf(['down', 'up', 'same']).isRequired };

const MD_COMPONENTS = {
  // 감소율 칸이면 막대 그래프로, 아니면 그대로
  td: ({ children, style }) => {
    const bar = parseBar(cellText(children));
    return <td style={style}>{bar ? <ReductionBar pct={bar.pct} kind={bar.kind} /> : children}</td>;
  },
  // 코드 블록·인라인 코드 — 넓은 글자를 2칸 상자에
  // 표시 줄(「◀ D1」 「◀ [1]」)이 있는 실행계획은 진단 탭과 같은 줄 강조 + 번호 배지로(사용자 요청 2026-10-11)
  code: ({ children, className }) => {
    const text = String(children == null ? '' : children).replace(/\n$/, '');
    return <code className={className}>{hasMarkedLines(text) ? <MarkedPlanText text={text} /> : monoWideText(text)}</code>;
  },
  // 설명 목록 머리(사례 「**[1]**」 · 리포트 참고 사항 「**N1**」)는 계획 줄의 배지와 같은 모양으로
  strong: ({ children }) => {
    const m = /^(?:\[(\d+)\]|([DN]\d+))$/.exec(cellText(children));
    if (!m) return <strong>{children}</strong>;
    const label = m[1] || m[2];
    return <span className={`stn-pl-badge static${label.startsWith('N') ? ' note' : ''}`}>{label}</span>;
  },
  // 「*(블록)*」 처럼 괄호로 감싼 기울임은 **단위**다 — 지표 이름 옆에 작은 글씨로(사용자 요청 2026-10-12). 그 밖의 기울임은 그대로
  em: ({ children }) => (/^\(.{1,12}\)$/.test(cellText(children)) ? <span className="stn-unit">{children}</span> : <em>{children}</em>),
  // 그림(사례의 설명용 이미지) — 창 폭에 맞추고, 누르면 새 탭에서 원래 크기로
  img: ({ src, alt }) => {
    // 폭·배경은 주소 조각에 있다(utils/caseMeta.figureSrc) — 사례의 그림이 아니면 기본(100% · 밝은 바탕)
    const st = figureStyleOf(src);
    return (
      <a href={String(src || '').replace(/#.*$/, '')} target="_blank" rel="noreferrer" className={`stn-md-figure bg-${st.bg}`} style={{ width: `${st.width}%` }}>
        <img src={src} alt={alt || ''} loading="lazy" />
      </a>
    );
  },
  // 표는 넓으면 가로로 스크롤
  table: ({ children }) => <div className="stn-md-tablewrap"><table>{children}</table></div>,
};

/** 마크다운 + 사례 글의 색·크기 표시(`{{red:…}}` 등 — utils/caseTextMarks). 모듈 상수라 렌더마다 새로 만들지 않는다 */
const REMARK_PLUGINS = [remarkGfm, remarkCaseMarks];

/**
 * 본문 마크다운 — **글이 바뀔 때만** 다시 그린다(2026-10-26). 부르는 쪽(사례 창 등)은 작업 탭을 오갈 때마다 다시 그려지는데,
 * 그때마다 react-markdown 이 문서 전체를 새로 풀어 탭 전환이 멈칫했다(긴 사례 하나에 10ms 넘게).
 */
const MarkdownBody = React.memo(function MarkdownBody({ markdown }) {
  return <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={MD_COMPONENTS}>{markdown.replace(/<br\s*\/?>/gi, ' · ')}</ReactMarkdown>;
});
MarkdownBody.propTypes = { markdown: PropTypes.string.isRequired };

/**
 * @param header  고정 줄 맨 위에 둘 것(사례 제목 등) — 본문과 함께 스크롤되지 않는다
 * @param inline  true 면 부르는 쪽 단추(toolbar)와 보기 방식(문서/원문)을 **한 줄**에(사례 상세, 2026-10-11)
 * @param lead    inline 줄의 왼쪽에 둘 것(사례의 공개 범위·상태·용도 등, 2026-10-24) — 있으면 단추들은 오른쪽 끝으로 간다
 * @param footer  본문 아래에 둘 것(사례의 과정 이력·지난 판, 2026-10-24) — **이 안에 둬야** 맨 아래까지 내려도 고정 줄이 따라온다
 *                (고정 줄은 sticky 라 이 틀이 끝나면 함께 밀려 올라간다 — 이력을 틀 밖에 두니 끝까지 내리면 제목·단추가 사라졌다)
 */
function ReportView({ markdown, loadingText, toolbar, header, inline, lead, footer }) {
  const { t } = useTranslation();
  const [raw, setRaw] = useState(false);
  // 단추 줄은 스크롤되지 않는다(사용자 요청 2026-10-10) — 부르는 쪽의 단추(toolbar)와 보기 방식을 한 덩어리로 위에 붙인다.
  // 불러오는 동안에도 그린다 — 빈 동안 단추가 사라지면 「다시 만들기」·범위 전환을 누를 수 없다
  const modeBar = markdown ? (
    <div className="stn-row stn-report-mode" role="group" aria-label={t('common:stn.reportMode')}>
      <button type="button" className={`app-btn${raw ? '' : ' on'}`} aria-pressed={!raw} onClick={() => setRaw(false)}>{t('common:stn.reportDoc')}</button>
      <button type="button" className={`app-btn${raw ? ' on' : ''}`} aria-pressed={raw} onClick={() => setRaw(true)}>{t('common:stn.reportRaw')}</button>
    </div>
  ) : null;
  return (
    <div className="stn-reportview">
      <div className="stn-report-sticky">
        {header}
        {inline ? (
          <div className="stn-row stn-report-bar">
            {lead ? <div className="stn-report-lead">{lead}</div> : null}
            {toolbar}{modeBar}
          </div>
        ) : <>{toolbar}{modeBar}</>}
      </div>
      {!markdown && <pre className="stn-pre stn-report">{loadingText}</pre>}
      {!markdown ? null : raw
        ? <pre className="stn-pre stn-report stn-fade">{monoWideText(markdown)}</pre>
        : (
          <div className="stn-md stn-fade">
            <MarkdownBody markdown={markdown} />
          </div>
        )}
      {footer}
    </div>
  );
}

ReportView.propTypes = { markdown: PropTypes.string, loadingText: PropTypes.string, toolbar: PropTypes.node, header: PropTypes.node, inline: PropTypes.bool, lead: PropTypes.node, footer: PropTypes.node };

export default ReportView;
export { MD_COMPONENTS };
